'use client';

import { Button } from '@/components/ui/button';
import { ChevronLeft, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Page } from '../../types/page';
import { getLocationIcon } from './location-icon';
import { LocationType } from '../../types/location';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { markdownToHtml } from '../../lib/markdown-to-html';
import {
  Collapsible,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { useState } from 'react';
import { Booking } from '../../types/booking';
import { BookingInfo } from './booking-info';
import { PageView } from '../../types/enum';
import { convertToMinutes, formattedDuration, getInitial } from '../../helper/utility';

interface ResourceSidebarProps {
  page: Page;
  resource: any;
  onBack: () => void;
  booking?: Booking;
  currentView?: PageView;
}

export function ResourceSidebar({
  page,
  resource,
  onBack,
  booking: oldBooking,
  currentView,
}: ResourceSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isDescriptionLong = resource.description?.length > 150;
  const [booking, setBooking] = useState<Booking>({ ...oldBooking } as Booking);

  return (
    <div className="p-6 space-y-2">
      <Button variant="ghost" onClick={onBack} className="mb-4 px-0">
        <ChevronLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      <div className="space-y-6">
        <div className="space-y-4">
          <Avatar className="w-20 h-20">
            <AvatarImage
              src={page.logo}
              alt={page.name}
              width={64}
              height={64}
            />
            <AvatarFallback>{getInitial(page.name)}</AvatarFallback>
          </Avatar>
          {currentView === 'reschedule' && booking ? (
            <>
              <h1 className="text-xl font-bold text-foreground">
                Reschedule booking
              </h1>
              <BookingInfo booking={booking}></BookingInfo>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold text-foreground">
                {resource.name}
              </h1>
              <Collapsible
                open={!isDescriptionLong || isOpen}
                onOpenChange={setIsOpen}
                className="w-full"
              >
                <div className="text-sm text-muted-foreground prose dark:prose-invert">
                  {isDescriptionLong && !isOpen ? (
                    <>
                      <div
                        className="line-clamp-6"
                        dangerouslySetInnerHTML={{
                          __html: markdownToHtml(resource.description),
                        }}
                      />
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="link"
                          className="h-auto p-0 mt-1 text-sm"
                        >
                          + Show more
                        </Button>
                      </CollapsibleTrigger>
                    </>
                  ) : (
                    <>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: markdownToHtml(resource.description),
                        }}
                      />
                      {isDescriptionLong && (
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="link"
                            className="h-auto p-0 mt-1 text-sm"
                          >
                            - Show less
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </>
                  )}
                </div>
              </Collapsible>
              {resource.durations && resource.durations.length > 0 && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 mr-2" />
                  <div className="flex gap-2">
                    {resource.durations.length > 0 && (
                      <Badge variant="secondary">
                        {formattedDuration(
                          convertToMinutes(resource.durations[0])
                        )}
                      </Badge>
                    )}
                    {resource.durations.length > 1 && (
                      <Badge variant="secondary">
                        +{resource.durations.length - 1} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              {resource.locations && resource.locations.length > 0 && resource.event_type !== 'round_robin' && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <span className="mr-2">
                    {resource.locations.length > 0 &&
                      getLocationIcon(
                        resource.locations[0].type as LocationType
                      )}
                  </span>
                  <div className="flex gap-2">
                    {resource.locations.length > 0 && (
                      <Badge variant="secondary">
                        {resource.locations[0].name}{' '}
                        {resource.durations[0].name}
                      </Badge>
                    )}
                    {resource.locations.length > 1 && (
                      <Badge variant="secondary">
                        +{resource.locations.length - 1} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
